import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel, PanelHeader} from '@Components/panel';
import {ThumbnailItemSelectionState} from '@Components/thumbnail-item';
import {Thumbnail} from '@Components/thumbnail';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {getAssetUrl} from '@Utils/s3.util';
import {useUpdateEffect} from 'ahooks';
import {getDefaultMorphAmountFromType, MorphType} from '@Libraries/fancy-text-library';
import {CircularProgressLoader} from '@Components/circular-progress-loader';
import {ContextualMenu} from '@Components/contextual-menu';
import {updateOptionsPanelHeight} from '@Components/poster-editor/library/poster-editor-library';
import {FancyTextCurveSliderOption} from '@Panels/poster-editor-fancytext-shape-panel/components/fancy-text-curve-slider-option';
import {getCompatibleImageFileExtension} from '@Utils/image.util';
import {ContextualMenuHorizontalScrollingSection} from '@Components/contextual-menu/components/contextual-menu-horizontal-scrolling-section';
import {ContextualMenuThumbnailItem} from '@Components/contextual-menu/components/contextual-menu-thumbnail-item';
import styles from './poster-editor-fancytext-shape-panel.module.scss';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromFancyTextItem} from '@Hooks/poster-editor/useGetKeyFromFancyTextItem';

interface PosterEditorFancytextShapePanelProps {
  panelId: string;
}

const FANCY_TEXT_SHAPES = [MorphType.NONE, MorphType.CURVE_UP, MorphType.CURVE_DOWN, MorphType.WAVE, MorphType.WEDGE_LEFT, MorphType.WEDGE_RIGHT, MorphType.ROOF];
const THUMBNAIL_BASE_URL = 'poster-editor-thumbnails/fancy-text-shapes/';

export default function PosterEditorFancytextShapePanel({...props}: PosterEditorFancytextShapePanelProps): ReactElement | null {
  const onClose = useDefaultPanelClose(props.panelId);
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeItemMorphType = useGetKeyFromFancyTextItem('morphType');
  const activeItemMorphAmount = useGetKeyFromFancyTextItem('morphAmount');
  const [isLoading, setIsLoading] = useState(false);
  const [tempValue, setTempValue] = useState<number>(0);
  const [isChanging, setIsChanging] = useState(false);
  const [isMorphAmountUpdating, setIsMorphAmountUpdating] = useState(false);

  useUpdateEffect((): void => {
    onClose();
  }, [activeItem]);

  useEffect((): void => {
    if (activeItem?.isFancyText()) {
      updateOptionsPanelHeight(props.panelId, activeItem.isMorphAmountApplicable() ? '246px' : '158px');
    }
  }, [activeItemId, activeItemMorphType]);

  if (!activeItem || !activeItem.isFancyText() || activeItemMorphType === undefined || activeItemMorphAmount === undefined) {
    return null;
  }

  const getHeader = (): ReactElement => {
    return <PanelHeader onClose={onClose} title={window.i18next.t('pmwjs_text_shape')} />;
  };

  const getTextFromType = (type: MorphType): string => {
    switch (type) {
      case MorphType.WEDGE_LEFT:
        return window.i18next.t('pmwjs_wedge_left');
      case MorphType.WEDGE_RIGHT:
        return window.i18next.t('pmwjs_wedge_right');
      case MorphType.ROOF:
        return window.i18next.t('pmwjs_roof');
      case MorphType.WAVE:
        return window.i18next.t('pmwjs_wave');
      case MorphType.CURVE_UP:
        return window.i18next.t('pmwjs_curve_up');
      case MorphType.CURVE_DOWN:
        return window.i18next.t('pmwjs_curve_down');
      case MorphType.NONE:
      default:
        return window.i18next.t('pmwjs_straight');
    }
  };

  const getURLFromType = (type: MorphType): string => {
    switch (type) {
      case MorphType.WEDGE_LEFT:
        return 'wedge_left';
      case MorphType.WEDGE_RIGHT:
        return 'wedge_right';
      case MorphType.ROOF:
        return 'roof';
      case MorphType.WAVE:
        return 'wave';
      case MorphType.CURVE_UP:
        return 'curve_up';
      case MorphType.CURVE_DOWN:
        return 'curve_down';
      case MorphType.NONE:
      default:
        return window.i18next.t('none');
    }
  };

  const getThumbnails = (): ReactElement[] => {
    return FANCY_TEXT_SHAPES.map((type) => {
      return (
        <ContextualMenuThumbnailItem
          key={type}
          thumbnail={
            isLoading && activeItemMorphType === type ? (
              <CircularProgressLoader classes="spacing-m-0" />
            ) : (
              <Thumbnail
                className="radius-8 spacing-p-r-1 spacing-p-l-1"
                data={{
                  type: ThumbnailType.IMAGE,
                  imageSrc: getAssetUrl(`${THUMBNAIL_BASE_URL}${getURLFromType(type)}.${getCompatibleImageFileExtension('webp')}`),
                }}
              />
            )
          }
          isSelected={type === activeItemMorphType}
          selectionState={ThumbnailItemSelectionState.BORDER}
          text={getTextFromType(type)}
          textClassName="content-body spacing-m-t-1"
          onClick={(): void => {
            if (type === activeItemMorphType || isLoading || isMorphAmountUpdating) {
              return;
            }
            setIsLoading(true);
            void activeItem
              .updateFromObject({
                morphType: Number(type),
                morphAmount: getDefaultMorphAmountFromType(Number(type)),
              })
              .finally(() => {
                setIsLoading(false);
              });
          }}
        />
      );
    });
  };

  const updateMorphAmount = (morphAmount: number): void => {
    setIsMorphAmountUpdating(true);
    void activeItem
      .updateFromObject({
        morphAmount,
      })
      .finally(() => {
        setIsMorphAmountUpdating(false);
        setIsChanging(false);
      });
  };

  const onChangeShapeCurve = (amount: number): void => {
    if (!isChanging) {
      setIsChanging(true);
    }
    setTempValue(amount);
  };

  const onChangeEndedShapeCurve = (amount: number): void => {
    updateMorphAmount(amount);
  };

  const getCurveAmountSlider = (): ReactElement => {
    return (
      <FancyTextCurveSliderOption
        panelId={props.panelId}
        onChangeShapeCurve={onChangeShapeCurve}
        onChangeShapeCurveEnded={onChangeEndedShapeCurve}
        morphType={activeItemMorphType}
        morphAmount={isChanging ? tempValue : activeItemMorphAmount}
        isDisabled={isMorphAmountUpdating}
      />
    );
  };

  const getContent = (): ReactElement => {
    return (
      <ContextualMenu id="fancy-text-shape-mobile-options" includeLeftRightPadding={false} contentClassName="spacing-m-0 spacing-p-t-1 spacing-p-b-4" hasMobileMorphingProperties>
        {getCurveAmountSlider()}
        <ContextualMenuHorizontalScrollingSection className={`${styles.list} spacing-p-l-4 spacing-p-r-4 flex-h-row spacing-m-0`}>
          {getThumbnails()}
        </ContextualMenuHorizontalScrollingSection>
      </ContextualMenu>
    );
  };

  return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
}
